// BaseHighchart.jsx
import { useKrCardContext } from '@/common/card/KrCardContext';
import { StringUtils } from '@/common/utils/StringUtils';
import { alpha, useTheme } from '@mui/material/styles';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import 'highcharts/highcharts-more';
import 'highcharts/modules/exporting';
import 'highcharts/modules/heatmap';
import 'highcharts/modules/offline-exporting';
import 'highcharts/modules/sankey';
import 'highcharts/modules/sunburst';
import 'highcharts/modules/tilemap';
import 'highcharts/modules/treemap';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDashboardContext } from '../DashboardContext';

const FONT = "'Manrope', system-ui, sans-serif";

export const BaseHighchart = ({ options }) => {
  const { registerChart } = useDashboardContext();

  const cardContext = useKrCardContext();
  let title = null;
  if (cardContext) {
    title = cardContext.title;
  }

  const theme = useTheme();
  const measureRef = useRef(null);
  const chartRef = useRef(null);
  const [measuredSize, setMeasuredSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    return registerChart(chartRef);
  }, [registerChart]);

  // measure
  useEffect(() => {
    const containerElement = measureRef.current;
    if (!containerElement) return;
    let animationFrameId = 0;

    const measureContainer = () => {
      const r = containerElement.getBoundingClientRect();
      setMeasuredSize({
        width: Math.max(0, Math.floor(r.width)),
        height: Math.max(0, Math.floor(r.height)),
      });
    };

    const resizeObserver = new ResizeObserver(() => {
      cancelAnimationFrame(animationFrameId);
      animationFrameId = requestAnimationFrame(measureContainer);
    });

    resizeObserver.observe(containerElement);
    measureContainer();
    return () => {
      cancelAnimationFrame(animationFrameId);
      resizeObserver.disconnect();
    };
  }, []);

  // animate size
  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (!chart) return;
    const { width, height } = measuredSize;
    if (width > 0 && height > 0) {
      chart.setSize(width, height, true);
    }
  }, [measuredSize.width, measuredSize.height]);

  const mergedOptions = useMemo(() => {
    const textPrimary = theme.palette.text.primary;
    const textSecondary = theme.palette.text.secondary;
    const tooltipBg = theme.palette.background.paper;

    const themedDefaults = {
      chart: {
        style: { fontFamily: FONT, color: textPrimary },
        backgroundColor: 'transparent',
        plotBackgroundColor: 'transparent',
      },
      title: { style: { color: textPrimary } },
      subtitle: { style: { color: textSecondary } },
      credits: { enabled: false },
      xAxis: {
        labels: { style: { color: textSecondary } },
        title: { style: { color: textSecondary } },
      },
      yAxis: {
        labels: {
          style: { color: textSecondary },
          formatter: function () {
            return StringUtils.formatCompactNumber(Number(this.value));
          },
        },
        title: { style: { color: textSecondary } },
      },
      legend: {
        itemStyle: { color: textPrimary },
        itemHoverStyle: { color: textPrimary },
      },
      tooltip: {
        backgroundColor: tooltipBg,
        style: { color: textPrimary, fontFamily: FONT },
        borderWidth: 0,
        shadow: false,
      },
      plotOptions: {
        series: {
          dataLabels: { style: { color: textPrimary, textOutline: 'none' } },
          states: {
            hover: { brightness: theme.palette.mode === 'dark' ? 0.1 : 0.08 },
            inactive: { opacity: 0.45 },
          },
        },
      },
      exporting: {
        enabled: false,
        chartOptions: {
          title: { text: options?.title?.text || title },
          chart: { height: null, width: null },
        },
      },
    };

    const callerSeries = options?.series || [];
    const baseColor = theme.palette.primary.main;

    // Generate palette of same size as caller series
    const palette = makeModulePalette(baseColor, callerSeries.length || 1, theme.palette.mode);

    // Respect any explicitly defined series colors
    let palettePtr = 0;
    const coloredSeries = callerSeries.map((s) => {
      if (s?.color) return s;
      const assigned = palette[palettePtr % palette.length];
      palettePtr += 1;

      // For stacked charts, apply subtle alpha variation
      const stacking =
        s?.stacking || options?.plotOptions?.series?.stacking || options?.plotOptions?.[s?.type || 'series']?.stacking;

      const maybeAlpha = stacking ? alpha(assigned, 0.94 - Math.min((palettePtr - 1) * 0.04, 0.24)) : assigned;

      return { ...s, color: maybeAlpha };
    });

    const colorsArray = palette; // default pool for Highcharts

    const injectedHeight = measuredSize.height > 0 ? measuredSize.height : (options?.chart?.height ?? 0);

    // To handle multiple y axes, we need to merge themed defaults for each axis individually
    const themedAxes = {};
    ['xAxis', 'yAxis'].forEach((axisKey) => {
      if (Array.isArray(options?.[axisKey])) {
        themedAxes[axisKey] = options[axisKey].map((axis) => Highcharts.merge(themedDefaults[axisKey], axis));
      }
    });

    return Highcharts.merge(
      themedDefaults,
      { ...options, ...themedAxes, series: coloredSeries, colors: colorsArray },
      { chart: { height: injectedHeight } },
    );
  }, [
    options,
    measuredSize.height,
    theme.palette.mode,
    theme.palette.text.primary,
    theme.palette.text.secondary,
    theme.palette.background.paper,
    theme.palette.primary?.main,
    title,
  ]);

  return (
    <div ref={measureRef} style={{ width: '100%', height: '100%', minWidth: 0, minHeight: 0 }}>
      {measuredSize.width > 0 && measuredSize.height > 0 && (
        <HighchartsReact
          ref={chartRef}
          highcharts={Highcharts}
          options={mergedOptions}
          containerProps={{ style: { width: '100%', height: '100%' } }}
          updateArgs={[true, true, { duration: 400 }]}
        />
      )}
    </div>
  );
};

/** ------------------ Color helpers ------------------ **/
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const hexToRgb = (hex) => {
  const normalized = hex.replace('#', '');
  const parsed =
    normalized.length === 3 ?
      normalized
        .split('')
        .map((ch) => ch + ch)
        .join('')
    : normalized;
  const num = parseInt(parsed, 16);
  return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
};

const rgbToHex = ({ r, g, b }) => {
  const toHex = (n) => n.toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const rgbToHsl = ({ r, g, b }) => {
  const rn = r / 255,
    gn = g / 255,
    bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const delta = max - min;

  let h = 0;
  if (delta !== 0) {
    if (max === rn) h = ((gn - bn) / delta) % 6;
    else if (max === gn) h = (bn - rn) / delta + 2;
    else h = (rn - gn) / delta + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  const l = (max + min) / 2;
  const s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));
  return { h, s, l };
};

const hslToRgb = ({ h, s, l }) => {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r1 = 0,
    g1 = 0,
    b1 = 0;

  if (0 <= h && h < 60) {
    r1 = c;
    g1 = x;
    b1 = 0;
  } else if (60 <= h && h < 120) {
    r1 = x;
    g1 = c;
    b1 = 0;
  } else if (120 <= h && h < 180) {
    r1 = 0;
    g1 = c;
    b1 = x;
  } else if (180 <= h && h < 240) {
    r1 = 0;
    g1 = x;
    b1 = c;
  } else if (240 <= h && h < 300) {
    r1 = x;
    g1 = 0;
    b1 = c;
  } else {
    r1 = c;
    g1 = 0;
    b1 = x;
  }

  return {
    r: Math.round((r1 + m) * 255),
    g: Math.round((g1 + m) * 255),
    b: Math.round((b1 + m) * 255),
  };
};

const shiftHue = (hex, degrees) => {
  const hsl = rgbToHsl(hexToRgb(hex));
  const shifted = { h: (hsl.h + degrees + 360) % 360, s: hsl.s, l: hsl.l };
  return rgbToHex(hslToRgb(shifted));
};

// 0, +1, -1, +2, -2 ...
const interleaveIndices = (n) => {
  const order = [0];
  let step = 1;
  while (order.length < n) {
    order.push(step);
    if (order.length < n) order.push(-step);
    step += 1;
  }
  return order;
};

// Palette generator: rotate hue around base, tweak lightness a bit
const makeModulePalette = (baseHex, count, mode = 'light') => {
  if (count <= 1) return [baseHex];

  const hueOffsets = [0, 40, -160, -10, 160, -90, 90, -20, -60, 72, -36];
  const offsets = [];
  for (let i = 0; offsets.length < count; i += 1) {
    offsets.push(hueOffsets[i % hueOffsets.length]);
  }

  // const baseHsl = rgbToHsl(hexToRgb(baseHex));
  const targetS = mode === 'dark' ? 0.58 : 0.56;
  const baseL = mode === 'dark' ? 0.56 : 0.48;
  const jitter = 0.06;

  const lightnessOrder = interleaveIndices(count);

  return offsets.map((deg, index) => {
    const rotated = shiftHue(baseHex, deg);
    const { h } = rgbToHsl(hexToRgb(rotated));
    const l = clamp(baseL + lightnessOrder[index] * (jitter / 2), 0.14, 0.86);
    const s = clamp(targetS, 0, 1);
    return rgbToHex(hslToRgb({ h, s, l }));
  });
};
